"use client";

import Offers from "./offers/offers";
import NameAndEmail from "./nameAndEmail/nameAndEmail";
import Address, { AddressForm } from "./address";
import OrderSummary from "./orderSummary/orderSummary";
import { Brand, Offer, Testimonial } from "@prisma/client";
import { useEffect, useState } from "react";
import { fetchOffers } from "./offers/actions";
import { useClientSideInfo } from "./useClientSideInfo";
import Payment from "./payment";
import PurchaseButton from "../purchaseButton/PurchaseButton";
import { FormProvider, useForm } from "react-hook-form";
import { AnimatePresence } from "framer-motion";
import StepIndicator from "./StepIndicator";
import Button from "./button";
import Hidden from "./Hidden";
import { useTracking } from "components/trackEvent";
import Testimonials from "./testimonials";
import Header from "./header";
import PaymentLogos from "./PaymentLogos";
import SkeletonLoader from "./SkeletonLoader";
import { createPurchaseIntentLead } from "./nameAndEmail/createPurchaseIntentLead";

const FullScreen = ({
  offerIds: initialOfferIds,
  brand,
  publishableStripeKey,
  internationalShippingOffer,
  testimonials,
}: {
  offerIds: Array<Offer["id"]>;
  brand: Brand;
  publishableStripeKey: string;
  internationalShippingOffer: Offer | null;
  testimonials: Array<Testimonial>;
}) => {
  const { setUserAttribute } = useTracking();
  const [offerIds, setOfferIds] = useState<Array<Offer["id"]>>(initialOfferIds);
  const [offers, setOffers] = useState<Awaited<ReturnType<typeof fetchOffers>>>(
    []
  );
  const methods = useForm<{
    address: AddressForm;
    name: string;
    email: string;
  }>({
    mode: "onBlur",
    defaultValues: {
      address: {
        validated: undefined,
      },
    },
  });
  const { trigger, watch } = methods;

  // need to track feature flags
  const clientSideInfo = useClientSideInfo(brand.id, offers);
  const [currentStep, setCurrentStep] = useState("contact");
  const [isLoading, setIsLoading] = useState(true);
  const [addressIsValid, setAddressIsValid] = useState(false);
  const [shippingRequired, setShippingRequired] = useState(false);

  const country = methods.watch("address.country");
  useEffect(() => {
    if (!country || !shippingRequired) {
      return;
    }

    if (country === "US" && internationalShippingOffer) {
      removeOffer(internationalShippingOffer?.id as Offer["id"]);
    } else if (country && country !== "US" && internationalShippingOffer) {
      setOfferIds([...offerIds, internationalShippingOffer.id]);
    }
  }, [country]);

  useEffect(() => {
    const shippingRequired = offers.some((offer) => offer.shippingRequired);
    setShippingRequired(shippingRequired);
  }, [offers]);

  useEffect(() => {
    (async () => {
      setOffers(await fetchOffers(offerIds));
      setIsLoading(false);
    })();
  }, [offerIds]);

  const removeOffer = (offerId: Offer["id"]) => {
    setOfferIds(offerIds.filter((id) => id !== offerId));
  };

  function capitalize(str: string) {
    if (typeof str !== "string") {
      return "";
    }
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  const addressChange = watch("address");
  useEffect(() => {
    (async () => {
      setTimeout(async () => {
        if (shippingRequired) {
          // delay until internaional shipping required updates
          const addressHasErrors = await trigger("address");
          setAddressIsValid(!addressHasErrors);
        }
      }, 100);
    })();
  }, [
    addressChange?.phone,
    addressChange?.addressLine1,
    addressChange?.country,
    addressChange?.city,
    addressChange?.postalCode,
    trigger,
  ]);

  return (
    <div className="bg-white min-h-[calc(100vh-89px)] overflow-hidden overscroll-none">
      <Header brand={brand} />
      <div className="inline md:hidden p-2 mx-auto pb-32">
        <StepIndicator
          currentStep={currentStep}
          steps={
            [
              "contact",
              shippingRequired ? "shipping" : undefined,
              "payment",
            ].filter((x) => x) as string[]
          }
          setCurrentStep={setCurrentStep}
        />
      </div>
      <div className="m-auto flex flex-col md:flex-row-reverse">
        <div className="bg-gray-50 md:w-1/2 md:p-12 xl:pr-64 p-4 text-sm">
          <SkeletonLoader isLoading={isLoading}>
            <h2 className="font-bold text-center text-xl">Order Summary</h2>
            <Offers offers={offers} />
            <OrderSummary
              offers={offers}
              isInternationalShipping={!!(country && country !== "US")}
            />
            <div className="md:inline hidden flex justify-center pt-8">
              <Testimonials testimonials={testimonials} />
            </div>
          </SkeletonLoader>
        </div>

        <FormProvider {...methods}>
          <div className="w-full md:w-1/2 md:p-12 p-4 mx-auto pb-32 md:pb-2 xl:pl-64 min-h-[calc(100vh-89px)]">
            <div className="hidden md:inline">
              <StepIndicator
                currentStep={currentStep}
                steps={
                  [
                    "contact",
                    shippingRequired ? "shipping" : undefined,
                    "payment",
                  ].filter((x) => x) as string[]
                }
                setCurrentStep={setCurrentStep}
              />
            </div>
            <h5 className="text-center text-lg font-bold">
              {capitalize(currentStep)}
            </h5>
            <AnimatePresence>
              <SkeletonLoader key="info" isLoading={isLoading}>
                <Hidden unless={currentStep === "contact"}>
                  <NameAndEmail />
                  <p className="text-xs pb-2 pt-5 md:h-2 text-gray-500 text-center">
                    Your information will not be shared with outside parties
                  </p>
                  <div className="w-1/2 m-auto">
                    <Button
                      testId="contact-next-button"
                      onClick={async () => {
                        let nameSuccess = await trigger("name");
                        let emailSuccess = await trigger("email");

                        if (!nameSuccess || !emailSuccess) {
                          return;
                        }

                        const name = methods.getValues("name");
                        const email = methods.getValues("email")?.toLowerCase();

                        // Not awaiting on purpose
                        createPurchaseIntentLead(
                          { name, email, currentStep },
                          clientSideInfo
                        ).then((lead) => {
                          setUserAttribute("leadId", lead?.id);
                        });

                        nameSuccess &&
                          emailSuccess &&
                          setCurrentStep(
                            shippingRequired ? "shipping" : "payment"
                          );
                      }}
                    >
                      Next
                    </Button>
                  </div>
                </Hidden>

                <Hidden unless={currentStep === "shipping"}>
                  <Address
                    onNext={() => setCurrentStep("payment")}
                    shippingRequired={shippingRequired}
                  />
                  <div className="w-1/2 m-auto">
                    <Button
                      testId="shipping-next-button"
                      onClick={async () => {
                        const addressValid = await trigger("address");
                        addressValid && setCurrentStep("payment");
                      }}
                      disabled={addressIsValid}
                    >
                      Next
                    </Button>
                  </div>
                </Hidden>
                <Hidden unless={currentStep === "payment"}>
                  <Payment publishableStripeKey={publishableStripeKey}>
                    {(cardElement, stripe) => (
                      <div className="flex justify-center">
                        <PurchaseButton
                          shippingRequired={shippingRequired}
                          brandName={brand.name}
                          disabled={
                            Object.keys(methods.formState.errors).length > 0
                          }
                          validateForm={trigger}
                          offers={offers}
                          brandId={brand.id}
                          clientSideInfo={clientSideInfo}
                          cardElement={cardElement}
                          brandAbbreviation={brand.abbreviation}
                          stripe={stripe}
                        />
                      </div>
                    )}
                  </Payment>
                </Hidden>

                <PaymentLogos />
              </SkeletonLoader>
              <div key="testimonials" className="md:invisible md:h-0">
                <Testimonials testimonials={testimonials} />
              </div>
            </AnimatePresence>
          </div>
        </FormProvider>
      </div>
    </div>
  );
};

export default FullScreen;
